import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../themes/colors';
import { t } from '../../locales/i18n';
import { verifyMagicLink } from '../../auth/auth-store';
import { SocketClient } from '../../web-socket/socket-client';

/**
 * Consumes a magic link.
 *
 * The token arrives in the URL fragment (`/#/auth/magic/:token`) so it is never
 * sent to a server or written into an access log. It is single-use server-side,
 * which is why this must fire exactly once — React 18's StrictMode runs effects
 * twice in development, and a second call would consume an already-spent token
 * and show the user a failure on a link that worked.
 */

const Screen = styled.main`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  min-height: 100dvh;
  padding: 2rem;
  text-align: center;
`;

const Message = styled.p`
  color: ${colors.grey};
  margin: 0;
`;

const Button = styled.button`
  background: ${colors.primary};
  border: none;
  border-radius: 6px;
  color: ${colors.white};
  font-size: 1.1rem;
  padding: 0.9rem 1.5rem;
`;

export function MagicLinkPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const consumed = useRef(false);

  useEffect(() => {
    if (!token || consumed.current) return;
    consumed.current = true;

    verifyMagicLink(token)
      .then(() => {
        SocketClient.getInstance().reconnect();
        navigate('/', { replace: true });
      })
      .catch(() => setFailed(true));
  }, [navigate, token]);

  if (failed) {
    return (
      <Screen>
        <Message>{t('auth.errors.invalidOrExpiredToken')}</Message>
        <Button type="button" onClick={() => navigate('/auth', { replace: true })}>
          {t('auth.signInAgain')}
        </Button>
      </Screen>
    );
  }

  return (
    <Screen>
      <Message>{t('auth.signingIn')}</Message>
    </Screen>
  );
}
